import {
  Box,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Popover,
  Select,
} from "@mui/material";
import { ReactNode, useEffect, useRef, useState } from "react";
import { DateTime } from "luxon";
import DateRangeCalendarFilter from "../DateRangeCalendarFilter";
import {
  ACTIVITY_TIMEFRAME_OPTIONS,
  ActivityTimeframeValue,
  calculateActivityDateRange,
} from "./activityTimeframe";

export interface ActivityFilterState {
  dateFrom: DateTime | null;
  dateTo: DateTime | null;
  userFilter: string;
  eventFilter: string;
}

export interface ActivityFilterActor {
  uri?: string | null;
  displayName?: string | null;
}

export const createDefaultActivityFilters = (): ActivityFilterState => {
  const { from, to } = calculateActivityDateRange("last7days");
  return { dateFrom: from, dateTo: to, userFilter: "", eventFilter: "" };
};

export interface ActivityFilterPanelProps {
  filters: ActivityFilterState;
  /** Must be referentially stable (a `useState` setter or `useCallback`). */
  onFiltersChange: (filters: ActivityFilterState) => void;
  actors: ActivityFilterActor[];
  changedKeys: string[];
  isLoading?: boolean;
  /** Filters reset back to their defaults whenever this value changes. */
  resetKey?: string;
  formatEventLabel?: (key: string) => string;
  children?: ReactNode;
}

/**
 * Event/user/timeframe filter bar shared by the Customer Experience and Customer Success
 * activity tabs. Data fetching stays with each app's own hooks; this owns only the filter UI.
 */
export default function ActivityFilterPanel({
  filters,
  onFiltersChange,
  actors,
  changedKeys,
  isLoading = false,
  resetKey,
  formatEventLabel = (key) => key,
  children,
}: Readonly<ActivityFilterPanelProps>) {
  const [timeframeFilter, setTimeframeFilter] =
    useState<ActivityTimeframeValue>("last7days");
  const [dateRangeAnchor, setDateRangeAnchor] = useState<HTMLElement | null>(
    null,
  );
  const timeframeFormControlRef = useRef<HTMLDivElement>(null);
  const previousResetKey = useRef(resetKey);

  useEffect(() => {
    if (previousResetKey.current === resetKey) return;
    previousResetKey.current = resetKey;
    setTimeframeFilter("last7days");
    setDateRangeAnchor(null);
    onFiltersChange(createDefaultActivityFilters());
  }, [resetKey, onFiltersChange]);

  const closeDateRangePicker = () => setDateRangeAnchor(null);

  const handleTimeframeChange = (value: ActivityTimeframeValue) => {
    setTimeframeFilter(value);

    if (value === "customrange") {
      setDateRangeAnchor(timeframeFormControlRef.current);
      return;
    }

    closeDateRangePicker();
    const { from, to } = calculateActivityDateRange(value);
    if (from || to) {
      onFiltersChange({ ...filters, dateFrom: from, dateTo: to });
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 2,
          mb: 3,
        }}
      >
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel>Event</InputLabel>
          <Select
            value={filters.eventFilter}
            label="Event"
            onChange={(e) =>
              onFiltersChange({ ...filters, eventFilter: e.target.value })
            }
            sx={{ backgroundColor: "background.paper" }}
          >
            <MenuItem value="">All Events</MenuItem>
            {changedKeys.map((key) => (
              <MenuItem key={key} value={key}>
                {formatEventLabel(key)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel>User</InputLabel>
          <Select
            value={filters.userFilter}
            label="User"
            onChange={(e) =>
              onFiltersChange({ ...filters, userFilter: e.target.value })
            }
            sx={{ backgroundColor: "background.paper" }}
          >
            <MenuItem value="">All Users</MenuItem>
            {actors.map((actor) => (
              <MenuItem key={actor.uri} value={actor.uri ?? ""}>
                {actor.displayName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 120 }} ref={timeframeFormControlRef}>
          <InputLabel>Timeframe</InputLabel>
          <Select
            value={timeframeFilter}
            label="Timeframe"
            onChange={(e) =>
              handleTimeframeChange(e.target.value as ActivityTimeframeValue)
            }
            sx={{ backgroundColor: "background.paper" }}
          >
            {ACTIVITY_TIMEFRAME_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {isLoading && (
        <LinearProgress
          color="secondary"
          aria-label="Loading activity data"
          role="progressbar"
        />
      )}

      {timeframeFilter === "customrange" && (
        <Popover
          open={Boolean(dateRangeAnchor)}
          anchorEl={dateRangeAnchor}
          onClose={closeDateRangePicker}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          aria-label="Select custom date range for activity filter"
          role="dialog"
          aria-modal="true"
        >
          <DateRangeCalendarFilter
            value={[filters.dateFrom, filters.dateTo]}
            onChange={(dates: (DateTime | null)[]) => {
              if (Array.isArray(dates) && dates.length === 2) {
                onFiltersChange({
                  ...filters,
                  dateFrom: dates[0],
                  dateTo: dates[1],
                });
              }
            }}
            onAccept={closeDateRangePicker}
            onCancel={closeDateRangePicker}
          />
        </Popover>
      )}

      <Box aria-live="polite" aria-atomic="false">
        {children}
      </Box>
    </Box>
  );
}
